/* eslint-disable ember/no-controller-access-in-routes, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { loadTemplateWithSections } from 'linced-app/utils/reporting';

export default class ViewRoute extends Route {
  // Services
  @service
  session;

  @service
  store;

  async model(params) {
    let { person } = this.modelFor('reporting.group.student');

    let studentReport =
      this.store.peekRecord(
        'reports/student-report',
        params.student_report_id,
      ) ||
      (await this.store.findRecord(
        'reports/student-report',
        params.student_report_id,
      ));

    let templateId = studentReport.belongsTo('reportTemplateId').id();
    let template = await loadTemplateWithSections(this.store, templateId);

    // Load student-report-sections
    let sections = [];
    if (studentReport) {
      sections = await this.store.query('reports/student-report-section', {
        studentReportId: studentReport.get('id'),
      });
    }

    // A published report with a populated snapshot renders entirely from each
    // section's publishedSnapshot (+ captured visuals), so the live subject /
    // milestone / progression preload below is skipped — loading it would
    // defeat the snapshot's point of not depending on live records (see
    // docs/reporting/publish-snapshot.md). Pre-13205 published reports
    // (snapshot null) and non-published reports still fall through to the load.
    let isPublishedWithSnapshot =
      studentReport.get('state') === StudentReportState.PUBLISHED &&
      sections.some((s) => s.get('publishedSnapshot'));

    // Preload subjects, progressions, levels and expectations for visual sections
    let contentSections = this.store
      .peekAll('reports/report-template-section')
      .filter(
        (section) =>
          section.belongsTo('reportTemplateId').id() === template.get('id') &&
          section.sectionType === ReportTemplateSectionType.CONTENT,
      );

    let subjectIds = new Set();
    contentSections.forEach((section) => {
      let config = section.get('configuration');
      if (!config) return;
      try {
        let parsed = typeof config === 'string' ? JSON.parse(config) : config;
        if (parsed.subjectId) {
          subjectIds.add(parsed.subjectId);
        }
      } catch (e) {
        // ignore
      }
    });

    let personId = person.get('personId') || person.get('id');

    if (subjectIds.size && !isPublishedWithSnapshot) {
      await Promise.all(
        Array.from(subjectIds).map((subjectId) =>
          Promise.all([
            this.store.findRecord('goals/subject', subjectId).catch(() => null),
            this.store
              .query('milestones/progression', {
                goalSubjectId: subjectId,
                personId,
                type: ProgressionType.PROGRESSION,
                latest: true,
              })
              .catch(() => null),
            this.store
              .query('milestones/level', { goalSubjectId: subjectId })
              .catch(() => null),
            this.store
              .query('milestones/expectation', { goalSubjectId: subjectId })
              .catch(() => null),
          ]),
        ),
      );
    }

    return { template, person, studentReport };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let groupCtrl = this.controllerFor('reporting.group');
    groupCtrl.set('isReviewRoute', true);
    groupCtrl.set('activeReport', model.template);

    let studentCtrl = this.controllerFor('reporting.group.student');
    studentCtrl.set('isStudentReviewRoute', true);

    controller.set('person', model.person);
    controller.set('template', model.template);
    controller.set('studentReport', model.studentReport);
    controller.set(
      'canVoidRecreate',
      studentCtrl.canVoidRecreate(model.template.id),
    );
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      this.controllerFor('reporting.group').set('isReviewRoute', false);
      this.controllerFor('reporting.group').set('activeReport', null);
      this.controllerFor('reporting.group.student').set(
        'isStudentReviewRoute',
        false,
      );
      controller.set('person', null);
      controller.set('template', null);
      controller.set('studentReport', null);
      controller.set('canVoidRecreate', false);
    }
  }
}
